import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MagnifyingGlassIcon, BarcodeIcon, CarIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import EquipmentSelector from "@/components/EquipmentSelector";
import PhotoUploader from "@/components/PhotoUploader";
import ImportCarGrModal from "@/components/ImportCarGrModal";
import EnglishVersionPanel from "@/components/EnglishVersionPanel";
import CarGrExportPanel from "@/components/CarGrExportPanel";
import { Field, TextInput, NumberInput, Select, FUEL_TYPES, TRANSMISSIONS, BODY_TYPES, DRIVE_TYPES, COLORS, EXTERIOR_COLOR_TYPES, INTERIOR_TYPES, NUMPLATES, OWNERS_OPTIONS } from "@/components/CarFormFields";
import { CarGrMakeCombobox, CarGrModelCombobox } from "@/components/CarGrBrandSelect";

const STATUS_OPTIONS = [
  { value: "available", label: "Διαθέσιμο" },
  { value: "reserved", label: "Κρατημένο" },
  { value: "sold", label: "Πωλήθηκε" },
];
const COLOR_OPTIONS = ["", ...COLORS];
const EXTERIOR_COLOR_TYPE_OPTIONS = ["", ...EXTERIOR_COLOR_TYPES];
const INTERIOR_TYPE_OPTIONS = ["", ...INTERIOR_TYPES];

// Πεδία υποχρεωτικά για έγκυρη αγγελία στο car.gr (REQUIRED_FIELDS στο cargr_exporter.py)
const CARGR_REQUIRED = [
  { key: "brand", label: "Μάρκα" },
  { key: "model", label: "Μοντέλο" },
  { key: "year", label: "Έτος", num: true },
  { key: "registration_month", label: "Μήνας ταξινόμησης", num: true },
  { key: "mileage_km", label: "Χιλιόμετρα", num: true },
  { key: "price_eur", label: "Τιμή (€)", num: true },
  { key: "fuel_type", label: "Καύσιμο" },
  { key: "transmission", label: "Κιβώτιο" },
  { key: "body_type", label: "Αμάξωμα" },
  { key: "engine_cc", label: "Κυβισμός (cc)", num: true },
  { key: "horsepower", label: "Ίπποι (hp)", num: true },
  { key: "doors", label: "Θύρες", num: true },
  { key: "seats", label: "Θέσεις", num: true },
  { key: "color_exterior", label: "Εξωτερικό χρώμα" },
];

const EMPTY = {
  brand: "",
  model: "",
  variant: "",
  year: new Date().getFullYear(),
  mileage_km: 0,
  price_eur: 0,
  fuel_type: "Βενζίνη",
  transmission: "Χειροκίνητο",
  body_type: "Λιμουζίνα / Sedan",
  engine_cc: 0,
  horsepower: 0,
  doors: 5,
  seats: 5,
  color_exterior: "",
  color_interior: "",
  drive_type: "FWD",
  co2_emissions: 0,
  registration_month: 1,
  vin: "",
  license_plate: "",
  location: "",
  description: "",
  excerpt: "",
  seo_title: "",
  seo_description: "",
  equipment: [],
  status: "available",
  featured: false,
  cargr_listing_id: "",
  // Extended vehicle attributes
  condition: "used",              // used | lightly_used | new
  euro_emission_class: "",        // Euro 3/4/5/6/6d
  // car.gr specific fields (also visible/editable from initial create)
  car_gr_exterior_color_type: "",
  car_gr_interior_type: "",
  car_gr_numplate: "",
  car_gr_previous_owners: 0,
  exclude_signature: false,
  description_must_include: "",
  car_gr_youtube_url: "",
  car_gr_circulation_tax: 0,
  // English / Polylang
  title_en: "",
  variant_en: "",
  description_en: "",
  excerpt_en: "",
  equipment_en: [],
  color_exterior_en: "",
  color_interior_en: "",
  location_en: "",
  fuel_type_en: "",
  transmission_en: "",
  body_type_en: "",
  seo_title_en: "",
  seo_description_en: "",
  translation_status: "no_english_version",
  include_in_polylang_export: true,
  // Visibility — both default to FALSE; operator opts in per car
  include_in_wp_sync: false,
  // car.gr Export
  include_in_car_gr_export: false,
  car_gr_update_interval: 7,
  car_gr_category_id: 0,
  car_gr_make_id: 0,
  car_gr_model_id: 0,
};

export default function AddCar() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [savedCarId, setSavedCarId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [vinInput, setVinInput] = useState("");
  const [vinLoading, setVinLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("manual"); // 'vin' | 'manual' | 'cargr'
  const [showImport, setShowImport] = useState(false);

  // Decode ?cargr=<base64-json> (small payloads) or fetch ?cargr_token=<uuid>
  // (large payloads stashed server-side) pushed by the car.gr bookmarklet
  useEffect(() => {
    const b64 = searchParams.get("cargr");
    const token = searchParams.get("cargr_token");
    if (!b64 && !token) return;

    const apply = (json) => {
      setForm((s) => {
        const merged = { ...s };
        const keys = ["brand", "model", "variant", "year", "mileage_km", "vin",
          "engine_cc", "horsepower", "doors", "seats",
          "color_exterior", "color_interior", "price_eur",
          "location", "cargr_listing_id", "description",
          "co2_emissions", "registration_month", "euro_emission_class",
          // car.gr extra fields
          "car_gr_exterior_color_type", "car_gr_interior_type", "car_gr_numplate",
          "car_gr_previous_owners", "car_gr_youtube_url", "car_gr_circulation_tax",
          // Server-resolved car.gr IDs (skips the combobox auto-snap race condition)
          "car_gr_make_id", "car_gr_model_id",
          // Sell-lead convert payload pre-fills customer info too
          "customer_name", "customer_phone", "customer_email"];
        for (const k of keys) {
          if (json[k] !== undefined && json[k] !== "" && json[k] !== null) merged[k] = json[k];
        }
        // Enum-validated fields — only apply if the value is a valid dropdown option
        if (json.fuel_type && FUEL_TYPES.includes(json.fuel_type)) merged.fuel_type = json.fuel_type;
        if (json.transmission && TRANSMISSIONS.includes(json.transmission)) merged.transmission = json.transmission;
        if (json.body_type && BODY_TYPES.includes(json.body_type)) merged.body_type = json.body_type;
        if (json.drive_type && ["FWD", "RWD", "4x4"].includes(json.drive_type)) merged.drive_type = json.drive_type;
        if (Array.isArray(json.equipment) && json.equipment.length) merged.equipment = json.equipment;
        return merged;
      });
      // Stage photo URLs to import after first save (handled when savedCarId is set below)
      if (Array.isArray(json.photo_urls) && json.photo_urls.length) {
        sessionStorage.setItem("cargr_photo_urls", JSON.stringify(json.photo_urls));
      }
      setMode("cargr");
      const filled = Object.keys(json).filter((k) => !k.startsWith("_") && json[k] !== "" && json[k] !== null).length;
      toast.success(`Εισήχθησαν ${filled} πεδία από car.gr`);
      if (json._duplicate_of) {
        toast.warning("Προσοχή: αυτή η αγγελία car.gr υπάρχει ήδη στον Στόλο — ελέγξτε πριν την ξανααποθηκεύσετε.", { duration: 10000 });
      }
    };

    if (b64) {
      try {
        apply(JSON.parse(decodeURIComponent(escape(atob(b64)))));
      } catch (e) {
        toast.error("Μη έγκυρα δεδομένα bookmarklet");
      }
      searchParams.delete("cargr");
      setSearchParams(searchParams, { replace: true });
    } else {
      api.get(`/import/cargr-payload/${token}`)
        .then((r) => apply(r.data))
        .catch(() => toast.error("Το import έληξε — πατήστε ξανά το bookmark στη σελίδα του car.gr"));
      searchParams.delete("cargr_token");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/catalog/brands").then((r) => setBrands(r.data.brands));
  }, []);

  useEffect(() => {
    if (form.brand) {
      api.get("/catalog/models", { params: { brand: form.brand } }).then((r) => setModels(r.data.models));
    } else {
      setModels([]);
    }
  }, [form.brand]);

  const [errors, setErrors] = useState({});
  const set = (k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
    setErrors((er) => (er[k] ? { ...er, [k]: false } : er));
  };

  // Memoised form view that exposes `id` to panels (needed by EnglishVersionPanel & CarGrExportPanel)
  const formWithId = useMemo(() => ({ ...form, id: savedCarId || undefined }), [form, savedCarId]);

  const lookupVin = async () => {
    if (!vinInput.trim()) return;
    setVinLoading(true);
    try {
      const r = await api.get(`/vin/${vinInput.trim().toUpperCase()}`);
      const d = r.data;
      if (!d.found) {
        toast.warning("Δεν βρέθηκαν στοιχεία για αυτό το VIN. Συμπληρώστε χειροκίνητα.");
      } else {
        setForm((s) => ({
          ...s,
          vin: d.vin,
          brand: d.brand && brands.includes(d.brand) ? d.brand : (d.brand || s.brand),
          model: d.model || s.model,
          year: d.year || s.year,
          body_type: d.body_type || s.body_type,
          engine_cc: d.engine_cc || s.engine_cc,
          horsepower: d.horsepower || s.horsepower,
          doors: d.doors || s.doors,
          fuel_type: d.fuel_type || s.fuel_type,
          transmission: d.transmission || s.transmission,
        }));
        toast.success("Στοιχεία VIN συμπληρώθηκαν αυτόματα");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία αναζήτησης VIN");
    } finally {
      setVinLoading(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    const errs = {};
    for (const f of CARGR_REQUIRED) {
      const v = form[f.key];
      if (f.num ? !(Number(v) > 0) : !(v && String(v).trim())) errs[f.key] = true;
    }
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Υποχρεωτικά πεδία για το car.gr: " + CARGR_REQUIRED.filter((f) => errs[f.key]).map((f) => f.label).join(", "));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year) || 0,
        mileage_km: Number(form.mileage_km) || 0,
        price_eur: Number(form.price_eur) || 0,
        engine_cc: Number(form.engine_cc) || 0,
        horsepower: Number(form.horsepower) || 0,
        doors: Number(form.doors) || 5,
        seats: Number(form.seats) || 5,
        co2_emissions: Number(form.co2_emissions) || 0,
        registration_month: Number(form.registration_month) || 1,
        car_gr_make_id: Number(form.car_gr_make_id) || 0,
        car_gr_model_id: Number(form.car_gr_model_id) || 0,
      };
      if (savedCarId) {
        const r = await api.put(`/cars/${savedCarId}`, payload);
        // Apply AI-generated fields (description / excerpt / SEO) returned from the server
        setForm((s) => ({
          ...s,
          description: r.data?.description ?? s.description,
          excerpt: r.data?.excerpt ?? s.excerpt,
          seo_title: r.data?.seo_title ?? s.seo_title,
          seo_description: r.data?.seo_description ?? s.seo_description,
        }));
        toast.success("Ενημερώθηκε");
      } else {
        const r = await api.post("/cars", payload);
        setSavedCarId(r.data.id);
        setPhotos(r.data.photos || []);
        // Apply AI-generated fields (description / excerpt / SEO) returned from the server
        setForm((s) => ({
          ...s,
          description: r.data?.description ?? s.description,
          excerpt: r.data?.excerpt ?? s.excerpt,
          seo_title: r.data?.seo_title ?? s.seo_title,
          seo_description: r.data?.seo_description ?? s.seo_description,
        }));
        if ((r.data?.description || "").trim() && !(payload.description || "").trim()) {
          toast.success("Αποθηκεύτηκε. Η περιγραφή και τα SEO δημιουργήθηκαν αυτόματα.");
        } else {
          toast.success("Αποθηκεύτηκε. Τώρα μπορείτε να προσθέσετε φωτογραφίες.");
        }
        // If the user came in via the car.gr bookmarklet, auto-download the staged photo URLs
        const staged = sessionStorage.getItem("cargr_photo_urls");
        if (staged) {
          sessionStorage.removeItem("cargr_photo_urls");
          try {
            const urls = JSON.parse(staged);
            if (Array.isArray(urls) && urls.length) {
              toast.info(`Λήψη ${urls.length} φωτογραφιών από car.gr...`);
              const resp = await api.post(`/cars/${r.data.id}/photos/import-urls`, { urls });
              const got = resp.data?.saved?.length || 0;
              if (got > 0) {
                toast.success(`Κατέβηκαν ${got} φωτογραφίες`);
                // Refresh photos list
                const fresh = await api.get(`/cars/${r.data.id}`);
                setPhotos(fresh.data.photos || []);
              } else {
                toast.warning("Δεν ήταν δυνατή η λήψη φωτογραφιών από το car.gr");
              }
            }
          } catch {
            toast.warning("Λήψη φωτογραφιών απέτυχε");
          }
        }
      }
    } catch (e) {
      toast.error("Αποτυχία αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="add-car-page">
      {/* Mode selector */}
      <div className="border border-black/10 grid grid-cols-3">
        <button
          type="button"
          onClick={() => setMode("vin")}
          data-testid="mode-vin"
          className={`p-5 text-left border-r border-black/10 transition-colors ${
            mode === "vin" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F9FAFB]"
          }`}
        >
          <BarcodeIcon size={26} weight="bold" />
          <div className="heading text-base font-bold mt-2">VIN Decoder</div>
          <div className={`text-xs mt-1 ${mode === "vin" ? "text-white/70" : "text-[#4B5563]"}`}>
            17-ψήφιος σειριακός
          </div>
        </button>
        <button
          type="button"
          onClick={() => setMode("manual")}
          data-testid="mode-manual"
          className={`p-5 text-left border-r border-black/10 transition-colors ${
            mode === "manual" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F9FAFB]"
          }`}
        >
          <CarIcon size={26} weight="bold" />
          <div className="heading text-base font-bold mt-2">Μάρκα / Μοντέλο</div>
          <div className={`text-xs mt-1 ${mode === "manual" ? "text-white/70" : "text-[#4B5563]"}`}>
            Επιλογή από κατάλογο
          </div>
        </button>
        <button
          type="button"
          onClick={() => { setMode("cargr"); setShowImport(true); }}
          data-testid="mode-cargr"
          className={`p-5 text-left transition-colors ${
            mode === "cargr" ? "bg-[#E60000] text-white" : "bg-white hover:bg-[#F9FAFB]"
          }`}
        >
          <DownloadSimpleIcon size={26} weight="bold" />
          <div className="heading text-base font-bold mt-2">Από car.gr</div>
          <div className={`text-xs mt-1 ${mode === "cargr" ? "text-white/70" : "text-[#4B5563]"}`}>
            Εισαγωγή με AI παραγωγή
          </div>
        </button>
      </div>

      {showImport && (
        <ImportCarGrModal
          onClose={() => setShowImport(false)}
          onImported={(data) => {
            const inEnum = (v, list) => v && list.includes(v) ? v : null;
            setForm((s) => ({
              ...s,
              brand: data.brand || s.brand,
              model: data.model || s.model,
              variant: data.variant || s.variant,
              year: data.year || s.year,
              mileage_km: data.mileage_km || s.mileage_km,
              price_eur: data.price_eur || s.price_eur,
              fuel_type: inEnum(data.fuel_type, FUEL_TYPES) || s.fuel_type,
              transmission: inEnum(data.transmission, TRANSMISSIONS) || s.transmission,
              body_type: inEnum(data.body_type, BODY_TYPES) || s.body_type,
              engine_cc: data.engine_cc || s.engine_cc,
              horsepower: data.horsepower || s.horsepower,
              doors: data.doors || s.doors,
              seats: data.seats || s.seats,
              color_exterior: data.color_exterior || s.color_exterior,
              color_interior: data.color_interior || s.color_interior,
              drive_type: inEnum(data.drive_type, DRIVE_TYPES) || s.drive_type,
              co2_emissions: data.co2_emissions || s.co2_emissions,
              registration_month: data.registration_month || s.registration_month,
              vin: data.vin || s.vin,
              license_plate: data.license_plate || s.license_plate,
              // description is intentionally NOT imported — it's generated by AI on save
              equipment: data.equipment && data.equipment.length ? data.equipment : s.equipment,
            }));
            setShowImport(false);
            setMode("manual");
          }}
        />
      )}

      {mode === "vin" && (
        <div className="border border-black/10 p-6 bg-[#F9FAFB]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-2">
            VIN Decoder
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              data-testid="vin-input"
              value={vinInput}
              onChange={(e) => setVinInput(e.target.value.toUpperCase())}
              placeholder="π.χ. WBA8E5C58JK982172"
              maxLength={17}
              className="flex-1 px-4 py-3 border border-black/20 bg-white outline-none focus:border-[#0055FF] font-mono text-base tracking-wider"
            />
            <button
              type="button"
              onClick={lookupVin}
              disabled={vinLoading || vinInput.length < 11}
              data-testid="vin-lookup-btn"
              className="btn-accent flex items-center gap-2"
            >
              <MagnifyingGlassIcon size={18} />
              {vinLoading ? "Αναζήτηση..." : "Αναζήτηση"}
            </button>
          </div>
          <div className="text-[11px] text-[#4B5563] mt-2 font-mono">
            Τα στοιχεία θα συμπληρωθούν αυτόματα. Μπορείτε να τα επεξεργαστείτε παρακάτω.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        {/* Basic info */}
        <Section title="Βασικά Στοιχεία" code="01">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Μάρκα" required error={errors.brand}>
              <CarGrMakeCombobox
                value={form.brand}
                makeId={form.car_gr_make_id}
                onPick={({ brand_name, make_id }) => {
                  setErrors((er) => ({ ...er, brand: false }));
                  setForm((s) => {
                    const sameBrand = (s.brand || "").toLowerCase() === (brand_name || "").toLowerCase();
                    return {
                      ...s,
                      brand: brand_name,
                      car_gr_make_id: make_id,
                      // Preserve the existing model only if we're just normalising the brand's case.
                      model: sameBrand ? s.model : "",
                      car_gr_model_id: sameBrand ? s.car_gr_model_id : 0,
                    };
                  });
                }}
                dataTestid="form-brand"
              />
            </Field>
            <Field label="Μοντέλο" required error={errors.model}>
              <CarGrModelCombobox
                value={form.model}
                modelId={form.car_gr_model_id}
                makeId={form.car_gr_make_id}
                onPick={({ model_name, model_id }) => {
                  setErrors((er) => ({ ...er, model: false }));
                  setForm((s) => ({ ...s, model: model_name, car_gr_model_id: model_id }));
                }}
                dataTestid="form-model"
              />
            </Field>
            <Field label="Έκδοση / Variant" hint={`${(form.variant || "").length}/60 χαρακτήρες (όριο car.gr)`}>
              <TextInput
                data-testid="form-variant"
                value={form.variant}
                maxLength={60}
                onChange={(e) => set("variant", e.target.value)}
                placeholder="π.χ. 1.6 TDI Highline"
              />
            </Field>
            <Field label="Έτος" required error={errors.year}>
              <NumberInput data-testid="form-year" value={form.year} onChange={(e) => set("year", e.target.value)} min={1950} max={2030} />
            </Field>
            <Field label="Μήνας ταξινόμησης" required error={errors.registration_month}>
              <NumberInput value={form.registration_month} onChange={(e) => set("registration_month", e.target.value)} min={1} max={12} />
            </Field>
            <Field label="Χιλιόμετρα" required error={errors.mileage_km}>
              <NumberInput data-testid="form-mileage" value={form.mileage_km} onChange={(e) => set("mileage_km", e.target.value)} min={0} />
            </Field>
            <Field label="Τιμή (€)" required error={errors.price_eur}>
              <NumberInput data-testid="form-price" value={form.price_eur} onChange={(e) => set("price_eur", e.target.value)} min={0} step={50} />
            </Field>
            <Field label="Κατάσταση">
              <Select
                data-testid="form-status"
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                options={STATUS_OPTIONS}
              />
            </Field>
            <Field label="Προβεβλημένο">
              <label className="flex items-center gap-2 px-3 py-2 border border-black/20 bg-white cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="form-featured" />
                <span className="text-sm">Featured στο feed</span>
              </label>
            </Field>
            <Field label="Υπογραφή" hint="Η υπογραφή από τις Ρυθμίσεις δεν θα προστίθεται σε site/car.gr">
              <label className="flex items-center gap-2 px-3 py-2 border border-black/20 bg-white cursor-pointer">
                <input type="checkbox" checked={!!form.exclude_signature} onChange={(e) => set("exclude_signature", e.target.checked)} data-testid="exclude-signature-toggle" />
                <span className="text-sm">Χωρίς αυτόματη υπογραφή</span>
              </label>
            </Field>
            <Field label="Αριθμός αγγελίας car.gr" hint="Επικολλήστε link ή αριθμό — κενό = χωρίς σύνδεση">
              <TextInput
                value={form.cargr_listing_id || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const m = v.match(/\/view\/(\d+)/);
                  set("cargr_listing_id", m ? m[1] : v.replace(/[^\d]/g, ""));
                }}
                placeholder="π.χ. 53024901"
                data-testid="form-cargr-listing-id"
              />
            </Field>
            <Field label="Υποχρεωτικά στην περιγραφή" hint="Θα αναφερθούν οπωσδήποτε στην περιγραφή">
              <TextInput
                value={form.description_must_include || ""}
                onChange={(e) => set("description_must_include", e.target.value)}
                placeholder="π.χ. ελληνικής αντιπροσωπείας, πρώτο χέρι"
                data-testid="form-must-include"
              />
            </Field>
          </div>
        </Section>

        {/* Technical */}
        <Section title="Τεχνικά Χαρακτηριστικά" code="02">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Καύσιμο" required error={errors.fuel_type}>
              <Select data-testid="form-fuel" value={form.fuel_type} onChange={(e) => set("fuel_type", e.target.value)} options={FUEL_TYPES} />
            </Field>
            <Field label="Κιβώτιο" required error={errors.transmission}>
              <Select data-testid="form-transmission" value={form.transmission} onChange={(e) => set("transmission", e.target.value)} options={TRANSMISSIONS} />
            </Field>
            <Field label="Αμάξωμα" required error={errors.body_type}>
              <Select value={form.body_type} onChange={(e) => set("body_type", e.target.value)} options={BODY_TYPES} />
            </Field>
            <Field label="Κυβισμός (cc)" required error={errors.engine_cc}>
              <NumberInput value={form.engine_cc} onChange={(e) => set("engine_cc", e.target.value)} min={0} />
            </Field>
            <Field label="Ίπποι (hp)" required error={errors.horsepower}>
              <NumberInput value={form.horsepower} onChange={(e) => set("horsepower", e.target.value)} min={0} />
            </Field>
            <Field label="CO₂ (g/km)">
              <NumberInput value={form.co2_emissions} onChange={(e) => set("co2_emissions", e.target.value)} min={0} />
            </Field>
            <Field label="Ρύποι / Euro">
              <Select
                value={form.euro_emission_class}
                onChange={(e) => set("euro_emission_class", e.target.value)}
                options={["", "Euro 3", "Euro 4", "Euro 5", "Euro 6", "Euro 6d"]}
              />
            </Field>
            <Field label="Κατάσταση Οχήματος">
              <select
                value={form.condition}
                onChange={(e) => set("condition", e.target.value)}
                data-testid="form-condition"
                className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
              >
                <option value="used">Μεταχειρισμένο</option>
                <option value="lightly_used">Ελαφρώς μεταχειρισμένο</option>
                <option value="new">Καινούργιο</option>
              </select>
            </Field>
            <Field label="Θύρες" required error={errors.doors}>
              <NumberInput value={form.doors} onChange={(e) => set("doors", e.target.value)} min={2} max={6} />
            </Field>
            <Field label="Θέσεις" required error={errors.seats}>
              <NumberInput value={form.seats} onChange={(e) => set("seats", e.target.value)} min={2} max={9} />
            </Field>
            <Field label="Μετάδοση">
              <Select value={form.drive_type} onChange={(e) => set("drive_type", e.target.value)} options={DRIVE_TYPES} />
            </Field>
            <Field label="Εξωτερικό χρώμα" required error={errors.color_exterior}>
              <Select value={form.color_exterior} onChange={(e) => set("color_exterior", e.target.value)} options={COLOR_OPTIONS} />
            </Field>
            <Field label="Τύπος βαφής">
              <Select
                value={form.car_gr_exterior_color_type}
                onChange={(e) => set("car_gr_exterior_color_type", e.target.value)}
                options={EXTERIOR_COLOR_TYPE_OPTIONS}
              />
            </Field>
            <Field label="Εσωτερικό χρώμα">
              <Select value={form.color_interior} onChange={(e) => set("color_interior", e.target.value)} options={COLOR_OPTIONS} />
            </Field>
            <Field label="Τύπος ταπετσαρίας">
              <Select
                value={form.car_gr_interior_type}
                onChange={(e) => set("car_gr_interior_type", e.target.value)}
                options={INTERIOR_TYPE_OPTIONS}
              />
            </Field>
            <Field label="Τοποθεσία">
              <TextInput value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="π.χ. Αθήνα" />
            </Field>
            <Field label="Πινακίδες (κυκλοφορία)">
              <Select
                value={form.car_gr_numplate}
                onChange={(e) => set("car_gr_numplate", e.target.value)}
                options={NUMPLATES}
              />
            </Field>
            <Field label="Προηγούμενοι ιδιοκτήτες" hint="Στέλνεται σε site/car.gr μόνο αν επιλέξετε αριθμό">
              <Select
                data-testid="prev-owners-select"
                value={String(form.car_gr_previous_owners || 0)}
                onChange={(e) => set("car_gr_previous_owners", Number(e.target.value) || 0)}
                options={OWNERS_OPTIONS}
              />
            </Field>
            <Field label="YouTube URL (παρουσίαση)">
              <TextInput
                value={form.car_gr_youtube_url}
                onChange={(e) => set("car_gr_youtube_url", e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
              />
            </Field>
            <Field label="Τέλη κυκλοφορίας (€)">
              <NumberInput
                value={form.car_gr_circulation_tax}
                onChange={(e) => set("car_gr_circulation_tax", Number(e.target.value) || 0)}
                min={0}
              />
            </Field>
          </div>
        </Section>

        {/* IDs */}
        <Section title="Ταυτότητα Οχήματος" code="03">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="VIN / Σειριακός" hint="17 χαρακτήρες">
              <TextInput data-testid="form-vin" value={form.vin} onChange={(e) => set("vin", e.target.value.toUpperCase())} className="font-mono" maxLength={17} />
            </Field>
            <Field label="Πινακίδες">
              <TextInput data-testid="form-plate" value={form.license_plate} onChange={(e) => set("license_plate", e.target.value.toUpperCase())} className="font-mono" />
            </Field>
          </div>
        </Section>

        {/* Description (optional — auto-generated by AI in the background if left empty) */}
        {isAdmin && (
        <Section title="Περιγραφή (προαιρετικό)" code="04">
          <div className="bg-[#F9FAFB] border border-black/10 px-4 py-2.5 mb-3 text-xs text-[#4B5563]">
            Αφήστε το κενό για δημιουργία περιγραφής + SEO μετά την αποθήκευση.
          </div>
          <textarea
            data-testid="form-description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={6}
            placeholder="Αν θέλετε να γράψετε δική σας περιγραφή πληκτρολογήστε εδώ."
            className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
          />
        </Section>
        )}

        {/* Equipment */}
        <Section title="Εξοπλισμός" code="05">
          <EquipmentSelector value={form.equipment} onChange={(v) => set("equipment", v)} />
        </Section>

        {/* Photos */}
        <Section title="Φωτογραφίες" code="06">
          <PhotoUploader carId={savedCarId} photos={photos} onChange={setPhotos} />
        </Section>

        {/* English / Polylang */}
        <Section title="Αγγλική Έκδοση (Polylang)" code="07">
          <EnglishVersionPanel form={formWithId} set={set} />
        </Section>

        {/* car.gr Export */}
        <Section title="car.gr Export" code="08">
          <CarGrExportPanel form={formWithId} set={set} />
        </Section>

        <div className="flex flex-col md:flex-row gap-3 items-center justify-end border-t border-black/20 pt-6">
          <button
            type="button"
            onClick={() => nav("/inventory")}
            className="btn-secondary"
            data-testid="cancel-btn"
          >
            Ακύρωση
          </button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="save-car-btn">
            {(() => {
              if (saving) return "Αποθήκευση...";
              if (savedCarId) return "Ενημέρωση";
              return "Αποθήκευση & Συνέχεια";
            })()}
          </button>
          {savedCarId && (
            <button type="button" onClick={() => nav("/inventory")} className="btn-accent" data-testid="finish-btn">
              Ολοκλήρωση
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Section({ title, code, children }) {
  return (
    <section className="border border-black/10 bg-white">
      <header className="px-6 py-4 border-b border-black/10 bg-[#F9FAFB] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-[#4B5563]">// {code}</span>
          <h3 className="heading font-bold text-base">{title}</h3>
        </div>
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}
